import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { MaybeRefOrGetter, onMounted, onUnmounted, toRef, toValue, watchEffect } from 'vue';

export async function waitForConnectionState(
  connection: HubConnection,
  state: HubConnectionState = HubConnectionState.Connected,
  timeout: number = 100,
  attempts: number = 10,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (connection.state === HubConnectionState.Connected) {
      resolve(true);
    } else if (attempts > 0) {
      setTimeout(async () => {
        resolve(waitForConnectionState(connection, state, timeout, attempts - 1));
      }, timeout);
    } else {
      reject(`picking up connection timed out after ${attempts} attempts`);
    }
  });
}

export interface HubSubscriptionOptions<T> {
  connection: HubConnection;
  event: string;
  onMessage: (value: T) => void;
  /** Reactive subscription key. Omit for hub registrations that take no key. */
  key?: MaybeRefOrGetter<string | undefined>;
  subscribe: (key: string | undefined) => Promise<string | undefined | void>;
  unsubscribe: (token: string | undefined) => Promise<void>;
}

function logSubscriptionError(event: string, action: 'subscribe' | 'unsubscribe', error: unknown) {
  console.error(`SignalR ${event}: failed to ${action}`, error);
}

export function useHubSubscription<T>(options: HubSubscriptionOptions<T>): void {
  const { connection, event, onMessage, key, subscribe, unsubscribe } = options;

  connection.on(event, onMessage);

  let subscribed = false;
  let token: string | undefined;

  async function ensureConnected() {
    if (connection.state === HubConnectionState.Disconnected) {
      await connection.start();
    }
    await waitForConnectionState(connection, HubConnectionState.Connected);
  }

  async function activate(currentKey: string | undefined) {
    subscribed = true;
    await ensureConnected();
    token = (await subscribe(currentKey)) ?? currentKey;
  }

  async function deactivate() {
    subscribed = false;
    const currentToken = token;
    token = undefined;
    if (connection.state === HubConnectionState.Connected) {
      await unsubscribe(currentToken);
    }
  }

  if (key !== undefined) {
    const id = toRef(key);

    // ponytail: subscribe/unsubscribe are fire-and-forget, so a key change during an
    // in-flight subscribe can leak the previous server-side subscription until unmount;
    // serialize them on a promise chain if that ever shows up.
    watchEffect(() => {
      if (subscribed) {
        deactivate().catch((error) => logSubscriptionError(event, 'unsubscribe', error));
      }
      if (id.value) {
        activate(id.value).catch((error) => logSubscriptionError(event, 'subscribe', error));
      }
    });
  } else {
    onMounted(() => {
      activate(undefined).catch((error) => logSubscriptionError(event, 'subscribe', error));
    });
  }

  onUnmounted(() => {
    connection.off(event, onMessage);
    if (subscribed) {
      deactivate().catch((error) => logSubscriptionError(event, 'unsubscribe', error));
    }
  });

  connection.onreconnected(() => {
    if (subscribed) {
      activate(toValue(key)).catch((error) => logSubscriptionError(event, 'subscribe', error));
    }
  });
}
